import React from "react";
import { Helmet } from "react-helmet";
import Header from "../Header";
import Footer from "../Footer";
import "./MobileUI.css";

export default function MobileUI() {
    const services = [
        {
            image: "assets/img/GameDevelopment/Creative-UI-Character-Design.png",
            title: "Mobile Game UI Design",
            description: "Design game menus, HUDs, lobbies, player profiles, rewards, store screens, onboarding, and gameplay interfaces for mobile-first play.",
            points: ["HUD and menus", "Lobby and profile UI", "Reward screens"]
        },
        {
            image: "assets/img/GameDevelopment/quiztopmobile.png",
            title: "Game App UX Flows",
            description: "Map player journeys for login, matchmaking, wallets, tournaments, achievements, onboarding, purchases, and LiveOps events.",
            points: ["Player journeys", "Flow mapping", "Conversion paths"]
        },
        {
            image: "assets/img/GameDevelopment/ludo-app-Feature.png",
            title: "Casual & Board Game UI",
            description: "Create clear, touch-friendly UI for card, board, puzzle, quiz, casual, fantasy, and social gaming experiences.",
            points: ["Touch-first UI", "Game controls", "Social features"]
        },
        {
            image: "assets/img/GameDevelopment/mobileui/designsystem.jpg",
            title: "Design Systems for Games",
            description: "Build scalable UI kits, reusable components, icon styles, color systems, screen states, and developer-ready design documentation.",
            points: ["UI kits", "Component states", "Developer handoff"]
        }
    ];

    const screenTypes = [
        { image: "assets/img/GameDevelopment/mobileui/onboarding.jpg", title: "Onboarding", description: "First-time player flow, sign-up, guest mode, permissions, tutorials, and early engagement." },
        { image: "assets/img/GameDevelopment/mobileui/Game-Lobby.jpg", title: "Game Lobby", description: "Room selection, matchmaking, tables, teams, game modes, friend invites, and player status." },
        { image: "assets/img/GameDevelopment/mobileui/gameplayHud.jpg", title: "Gameplay HUD", description: "Scores, timers, controls, health, progress, actions, powerups, and core game feedback." },
        { image: "assets/img/GameDevelopment/mobileui/store.jpg", title: "Wallet & Store", description: "Coins, gems, purchases, offers, subscriptions, rewards, inventory, and transaction history." },
        { image: "assets/img/GameDevelopment/mobileui/leaderboard.jpg", title: "Leaderboard", description: "Rankings, seasons, achievements, player stats, tournament results, and competitive feedback." },
        { image: "assets/img/GameDevelopment/mobileui/liveops.jpg", title: "LiveOps Events", description: "Daily rewards, missions, banners, campaigns, events, notifications, and content updates." }
    ];

    const designAssets = [
        "Mobile Game UI",
        "Game HUD Design",
        "Game Lobby UI",
        "Wallet UI",
        "Tournament UI",
        "Reward Screens",
        "Leaderboard UI",
        "Onboarding Screens",
        "Store Screens",
        "UI Style Guide",
        "Clickable Prototype",
        "Developer Handoff"
    ];

    const process = [
        { step: "01", title: "UX Discovery", description: "We study game type, target players, monetization model, device targets, player journey, and key engagement loops." },
        { step: "02", title: "Flow & Wireframes", description: "We plan screen flows, navigation, user actions, game states, wallet paths, onboarding, and gameplay UI structure." },
        { step: "03", title: "Visual UI Design", description: "We create polished mobile screens, HUDs, game menus, components, icons, typography, color systems, and screen variants." },
        { step: "04", title: "Prototype & Review", description: "We prepare clickable prototypes, interaction notes, edge states, responsive behavior, and iteration-ready feedback cycles." },
        { step: "05", title: "Handoff & Support", description: "We deliver assets, UI kits, component specs, spacing rules, export files, and developer support for implementation." }
    ];

    const benefits = [
        { icon: "fa-solid fa-gamepad", title: "Gaming UI Focus", description: "We design around player motivation, game clarity, reward loops, monetization, and repeated daily usage." },
        { icon: "fa-solid fa-mobile-screen", title: "Mobile-First Screens", description: "Layouts are built for thumb reach, small screens, readable actions, and responsive mobile behavior." },
        { icon: "fa-solid fa-layer-group", title: "Scalable UI Systems", description: "Reusable components, states, and design rules help teams ship new screens and events faster." },
        { icon: "fa-solid fa-code", title: "Developer-Ready Handoff", description: "Designs include structured assets, specs, states, flows, and implementation guidance for faster development." }
    ];

    const gallery = [
        { image: "assets/img/GameDevelopment/mobileui/quiz.jpg", title: "Quiz Game UI" },
        { image: "assets/img/GameDevelopment/mobileui/board.jpg", title: "Board Game UI" },
        { image: "assets/img/GameDevelopment/mobileui/fantacy.jpg", title: "Fantasy App UI" },
        { image: "assets/img/GameDevelopment/mobileui/kid.jpg", title: "Kids Game UI" },
        { image: "assets/img/GameDevelopment/mobileui/sport.jpg", title: "Fantasy Sports UI" },
        { image: "assets/img/GameDevelopment/mobileui/trivia.jpg", title: "Trivia App UI" },
        { image: "assets/img/GameDevelopment/mobileui/casual.jpg", title: "Casual Game UI" },
        { image: "assets/img/GameDevelopment/mobileui/gameplay.jpg", title: "Gameplay Screen UI" }
    ];

    const imageStory = [
        {
            image: "assets/img/GameDevelopment/fantasy-basketball-app-img.png",
            title: "Fantasy & Contest Apps",
            description: "Player cards, contest lists, team builder screens, wallet flows, scoring summaries, and match-center UI."
        },
        {
            image: "assets/img/GameDevelopment/card-game1.png",
            title: "Card & Casino UI",
            description: "Table screens, bet actions, timers, hand controls, store screens, reward feedback, and game result screens."
        },
        {
            image: "assets/img/GameDevelopment/endless-runner-game-thumbnail.png",
            title: "Casual Game Interfaces",
            description: "Fast menus, upgrade paths, powerups, level maps, mission screens, streak rewards, and one-hand interaction flows."
        }
    ];

    const faqs = [
        {
            question: "What mobile UI design services does Oengines provide?",
            answer: "Oengines provides mobile game UI, app UX flows, game HUDs, lobbies, wallet screens, reward flows, design systems, prototypes, and developer-ready handoff."
        },
        {
            question: "Can you design UI for mobile games and gaming apps?",
            answer: "Yes. We design interfaces for casual games, board games, card games, fantasy apps, tournament apps, game companion apps, and LiveOps products."
        },
        {
            question: "Do you provide clickable prototypes?",
            answer: "Yes. We can prepare clickable prototypes for onboarding, gameplay flow, wallet paths, tournaments, rewards, store screens, and major user journeys."
        },
        {
            question: "Can developers implement your designs easily?",
            answer: "Yes. We provide UI kits, components, screen states, assets, spacing rules, responsive notes, and design handoff support."
        }
    ];

    const structuredData = {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": "Mobile UI Design for Gaming Apps",
        "provider": {
            "@type": "Organization",
            "name": "Oengines Games LLP",
            "url": "https://www.oengines.com"
        },
        "serviceType": "Mobile UI UX Design",
        "areaServed": "Worldwide",
        "description": "Mobile UI and UX design services for games, gaming apps, game HUDs, lobbies, wallets, rewards, tournaments, LiveOps, prototypes, and design systems.",
        "offers": services.map((service) => ({
            "@type": "Offer",
            "itemOffered": {
                "@type": "Service",
                "name": service.title,
                "description": service.description
            }
        }))
    };

    return (
        <>
            <Helmet>
                <title>Mobile UI Design for Games & Gaming Apps | Oengines</title>
                <meta name="description" content="Oengines Games LLP designs mobile UI/UX for games and gaming apps, including HUDs, lobbies, wallets, rewards, leaderboards, tournaments, onboarding, prototypes, and design systems." />
                <meta name="keywords" content="mobile UI design, game UI design, mobile game UI, gaming app UI UX, game HUD design, game lobby UI, mobile app UI design, game design system" />
                <link rel="canonical" href="https://www.oengines.com/mobile-ui-design" />
                <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />
            </Helmet>
            <Header />
            <main className="mobile-ui-page">
                <section className="mobile-ui-hero">
                    <div className="container">
                        <div className="row align-items-center g-4">
                            <div className="col-lg-7">
                                <span className="mobile-ui-eyebrow">Mobile UI for Gaming Products</span>
                                <h1>Design Mobile Game UI That Players Understand Fast</h1>
                                <p>Oengines Games LLP designs mobile UI/UX for games and gaming apps, including HUDs, lobbies, wallets, rewards, leaderboards, tournaments, onboarding, LiveOps screens, and design systems.</p>
                                <div className="mobile-ui-actions">
                                    <a href="/contact" className="as-btn">Start UI Design <i className="fa-regular fa-arrow-right ms-2"></i></a>
                                    <a href="tel:+912614054978" className="mobile-ui-call">Call: (0261) - 4054978</a>
                                </div>
                                <div className="mobile-ui-hero-stats">
                                    <div><strong>HUD</strong><span>Gameplay UI</span></div>
                                    <div><strong>UX</strong><span>Player Flow</span></div>
                                    <div><strong>Kit</strong><span>Design System</span></div>
                                </div>
                            </div>
                            <div className="col-lg-5">
                                <div className="mobile-ui-hero-gallery">
                                    <img className="mobile-ui-main-shot" src="assets/img/GameDevelopment/Creative-UI-Character-Design.png" alt="Mobile game UI design" />
                                    <div className="mobile-ui-mini-grid">
                                        <img src="assets/img/GameDevelopment/quiztopmobile.png" alt="Quiz mobile UI" />
                                        <img src="assets/img/GameDevelopment/ludo-app-Feature.png" alt="Ludo mobile UI" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="mobile-ui-service-section space">
                    <div className="container">
                        <div className="mobile-ui-heading text-center">
                            <span>Mobile UI Services</span>
                            <h2>Game UI/UX Design Services for Mobile Players</h2>
                            <p>We design screens that help players start quickly, understand gameplay, manage rewards, join events, and return often.</p>
                        </div>
                        <div className="row g-4">
                            {services.map((service) => (
                                <div className="col-lg-3 col-md-6" key={service.title}>
                                    <article className="mobile-ui-card">
                                        <img src={service.image} alt={service.title} />
                                        <div>
                                            <h3>{service.title}</h3>
                                            <p>{service.description}</p>
                                            <ul>
                                                {service.points.map((point) => <li key={point}>{point}</li>)}
                                            </ul>
                                        </div>
                                    </article>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="mobile-ui-screen-section space">
                    <div className="container">
                        <div className="mobile-ui-heading text-center">
                            <span>Screen Coverage</span>
                            <h2>Important Mobile Game Screens We Design</h2>
                            <p>Gaming UI needs clear hierarchy across short sessions, intense gameplay moments, reward flows, monetization paths, and social competition.</p>
                        </div>
                        <div className="mobile-ui-screen-grid">
                            {screenTypes.map((screen) => (
                                <article className="mobile-ui-screen-card" key={screen.title}>
                                    <img src={screen.image} alt={`${screen.title} UI`} />
                                    <h3>{screen.title}</h3>
                                    <p>{screen.description}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="mobile-ui-gallery-section space">
                    <div className="container">
                        <div className="mobile-ui-heading text-center">
                            <span>Visual Direction</span>
                            <h2>Image-Led UI Concepts for Gaming Experiences</h2>
                        </div>
                        <div className="mobile-ui-gallery-grid">
                            {gallery.map((item) => (
                                <figure className="mobile-ui-gallery-card" key={item.title}>
                                    <img src={item.image} alt={item.title} />
                                    <figcaption>{item.title}</figcaption>
                                </figure>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="mobile-ui-showcase-section space">
                    <div className="container">
                        <div className="mobile-ui-heading text-center">
                            <span>UI Use Cases</span>
                            <h2>Mobile UI Direction for Different Game Products</h2>
                            <p>Every game category needs a different screen rhythm. We shape the UI around player intent, session length, competition, monetization, and repeat engagement.</p>
                        </div>
                        <div className="mobile-ui-showcase-grid">
                            {imageStory.map((item) => (
                                <article className="mobile-ui-showcase-card" key={item.title}>
                                    <img src={item.image} alt={item.title} />
                                    <div>
                                        <h3>{item.title}</h3>
                                        <p>{item.description}</p>
                                    </div>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="mobile-ui-assets-section space">
                    <div className="container">
                        <div className="mobile-ui-assets-panel">
                            <div className="mobile-ui-heading mb-0">
                                <span>Design Deliverables</span>
                                <h2>Everything Needed for Mobile Game UI Handoff</h2>
                                <p>We deliver clear design assets and documentation so developers can implement screens, states, components, and interactions with less confusion.</p>
                            </div>
                            <div className="mobile-ui-assets-list">
                                {designAssets.map((asset) => <span key={asset}>{asset}</span>)}
                            </div>
                        </div>
                    </div>
                </section>

                <section className="mobile-ui-process-section space">
                    <div className="container">
                        <div className="mobile-ui-heading text-center">
                            <span>Design Process</span>
                            <h2>From Game Flow to Developer-Ready Mobile UI</h2>
                        </div>
                        <div className="mobile-ui-process-grid">
                            {process.map((item) => (
                                <article className="mobile-ui-process-card" key={item.step}>
                                    <span>{item.step}</span>
                                    <h3>{item.title}</h3>
                                    <p>{item.description}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="mobile-ui-benefit-section space">
                    <div className="container">
                        <div className="mobile-ui-heading text-center">
                            <span>Why Choose Oengines</span>
                            <h2>Mobile UI Design Backed by Game Product Thinking</h2>
                        </div>
                        <div className="row g-4">
                            {benefits.map((item) => (
                                <div className="col-lg-3 col-md-6" key={item.title}>
                                    <article className="mobile-ui-benefit-card">
                                        <span><i className={item.icon}></i></span>
                                        <h3>{item.title}</h3>
                                        <p>{item.description}</p>
                                    </article>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="mobile-ui-faq-section space">
                    <div className="container">
                        <div className="mobile-ui-heading text-center">
                            <span>FAQ</span>
                            <h2>Mobile Game UI Design Questions</h2>
                        </div>
                        <div className="mobile-ui-faq-grid">
                            {faqs.map((item) => (
                                <article className="mobile-ui-faq-card" key={item.question}>
                                    <h3>{item.question}</h3>
                                    <p>{item.answer}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="mobile-ui-cta">
                    <div className="container">
                        <div className="mobile-ui-cta-box">
                            <div>
                                <span>Design Better Game Screens</span>
                                <h2>Plan your mobile game UI with Oengines.</h2>
                                <p>Share your game concept, screens, player flow, monetization path, and target devices. We will help shape a clear mobile UI direction.</p>
                            </div>
                            <a href="/contact" className="as-btn">Book UI Consultation <i className="fa-regular fa-arrow-right ms-2"></i></a>
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </>
    );
}
